import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { allowanceLabel, type AllowanceUnit } from "@talenthop/shared";
import { Alert, Card, ShieldCheck } from "../../components/icons.js";
import { useOrgPlans } from "./planUsage.js";

export interface ChargeLine {
  label: string;
  value: string;
}

interface Props {
  title: string;
  subtitle?: string;
  lines: ChargeLine[];
  /** What this action uses from the organisation's plan allowance (0094). */
  unit: AllowanceUnit;
  count: number;
  confirmLabel: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Usage-confirmation dialog (0094). Every billable action confirms ONCE per batch, showing the
 * organisation's remaining allowance before and after — in jobs / resumes / AI interviews, never
 * credits. Buying lives on Plans & usage; a 402 shortfall from the action endpoint surfaces
 * through `error` with a one-click jump there.
 *
 * Orgs that still hold a pre-plans prepaid balance keep working past their allowance (the server
 * falls back to it), so "not enough left" only blocks when there is no such balance.
 */
export function ConfirmChargeModal({
  title,
  subtitle,
  lines,
  unit,
  count,
  confirmLabel,
  busy,
  error,
  onConfirm,
  onCancel,
}: Props): ReactNode {
  const navigate = useNavigate();
  const { data } = useOrgPlans();

  const remaining = data?.usage[unit].remaining ?? null;
  const coveredByPlan = remaining != null && remaining >= count;
  const prepaid = (data?.legacyBalance ?? 0) > 0;
  const short = remaining != null && !coveredByPlan && !prepaid;
  const shortfallError = Boolean(error && /not enough|left|budget/i.test(error));

  return (
    <div className="rq-modal-scrim" role="dialog" aria-modal="true" onClick={busy ? undefined : onCancel}>
      <div className="rq-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rq-modal-h">
          <div className="t">{title}</div>
          {subtitle && <div className="d">{subtitle}</div>}
        </div>

        <div className="rq-modal-b">
          <div className="rq-modal-total">
            <span className="l">This will use</span>
            <span className="n tnum">{allowanceLabel(unit, count)}</span>
          </div>

          {lines.map((l, i) => (
            <div key={i} className="rq-line">
              <span className="k">{l.label}</span>
              <span className="v tnum">{l.value}</span>
            </div>
          ))}
          {remaining != null && (
            <>
              <div className="rq-line">
                <span className="k">Left now</span>
                <span className="v tnum">{allowanceLabel(unit, remaining)}</span>
              </div>
              {coveredByPlan && (
                <div className="rq-line strong">
                  <span className="k">Left after</span>
                  <span className="v tnum">{allowanceLabel(unit, remaining - count)}</span>
                </div>
              )}
              {!coveredByPlan && prepaid && (
                <div className="rq-line">
                  <span className="k">Paid from</span>
                  <span className="v">your prepaid balance</span>
                </div>
              )}
            </>
          )}

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              marginTop: 14,
              fontSize: 12,
              color: "var(--ink-mute)",
              lineHeight: 1.5,
            }}
          >
            <ShieldCheck />
            <span>
              Used once this runs. An interview that can't be assessed because of a system fault is
              returned to your allowance automatically.
            </span>
          </div>

          {short && (
            <div className="banner warn" style={{ marginTop: 12 }}>
              <Alert />
              <span>
                {`This needs ${allowanceLabel(unit, count)} — your organisation has ${remaining} left. `}
                {data?.canBuy ? "Buy a plan and come back — this screen holds your work." : "Ask your admin to buy a plan."}
              </span>
            </div>
          )}

          {(error ?? null) && (
            <div className="banner crit" style={{ marginTop: 12 }}>
              <Alert />
              <span>
                {error}
                {shortfallError && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="rq-btn ghost"
                      style={{ padding: "2px 8px", fontSize: 12 }}
                      onClick={() => navigate("/app/plans")}
                    >
                      Open Plans
                    </button>
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="rq-modal-f">
          <button type="button" className="rq-btn ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          {short ? (
            <button type="button" className="rq-btn primary" onClick={() => navigate("/app/plans")}>
              <Card /> See plans
            </button>
          ) : (
            <button type="button" className="rq-btn primary" disabled={Boolean(busy)} onClick={onConfirm}>
              <Card /> {busy ? "Processing…" : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
